/**
 * The Solutions tree — every tool of every Solution, and who may see it
 * (ADR-0048 grants, Developer+ and Administrator entries, ADR-0050
 * deployments). One list for the sidebar (`NavbarPanel`) and Home's "Your
 * tools" (ADR-0070), so the two can never disagree. Placement is not
 * permission: routes and the API re-check every request.
 */

import type { ReactNode } from "react";
import {
  IconArchive,
  IconChecklist,
  IconClipboardCheck,
  IconFileImport,
  IconHeadset,
  IconLayoutGrid,
  IconMessageSearch,
  IconPencilPlus,
  IconRobot,
  IconRosetteDiscountCheck,
  IconSearch,
  IconShieldCheck,
  IconStack2,
  IconStethoscope,
  IconTicket,
  IconUserCheck,
} from "@tabler/icons-react";
import { TICKETS_PATH } from "@/features/admin/ticketsPath";

export interface ModuleEntry {
  label: string;
  path: string;
  icon: ReactNode;
  /** A roadmap item: opens a placeholder and wears a "Soon" badge. */
  soon?: boolean;
  /** Extra prefixes that also mean "you are here" — for an entry whose
   *  destination is a hub with several URLs. Defaults to `path` alone. */
  also?: string[];
  /** The agent this entry opens (ADR-0048); shown only when granted. */
  agent?: string;
  /** Also needs Developer+ — for an entry whose route is role-gated on top of
   *  the agent grant, so a User is not offered a page that refuses them. */
  developerPlus?: boolean;
  /** The Administrator's only — an entry that is not an agent (ADR-0069). */
  adminOnly?: boolean;
}

/** Prefix match on whole segments, so `/projects` does not claim
 *  `/projects-archive`, and a hub's row lights for any of its tabs. */
export function isActive(pathname: string, entry: { path: string; also?: string[] }): boolean {
  return [entry.path, ...(entry.also ?? [])].some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export interface SolutionGroup {
  /** The Solution key (`features/auth/agents.ts`). */
  solution: string;
  label: string;
  icon: ReactNode;
  entries: ModuleEntry[];
}

/** The Solutions tree, trimmed to the agents the caller may use (ADR-0048)
 *  and, for a Developer+ or an Administrator's entry, to their role. An
 *  Administrator's entry shows only in a Solution the deployment offers
 *  (ADR-0050). */
export function visibleSolutionGroups(
  agents: readonly string[],
  isDeveloperPlus: boolean,
  isAdmin: boolean,
  offered: readonly string[] | null | undefined,
): SolutionGroup[] {
  return solutionGroups()
    .map((group) => ({
      ...group,
      entries: group.entries.filter(
        (e) =>
          (!e.agent || agents.includes(e.agent)) &&
          (!e.developerPlus || isDeveloperPlus) &&
          (!e.adminOnly || (isAdmin && (!offered || offered.includes(group.solution)))),
      ),
    }))
    .filter((group) => group.entries.length > 0);
}

/** The full Solutions tree — adding a solution is an entry, not a nav
 *  rewrite. Release validation is one entry: its page holds the runs and the
 *  standalone risk assessment as tabs (ADR-0064). */
export function solutionGroups(): SolutionGroup[] {
  return [
    {
      solution: "base",
      label: "Base",
      icon: <IconStack2 size={18} />,
      entries: [
        {
          label: "Explore",
          path: "/solutions/explore",
          agent: "explore",
          icon: <IconMessageSearch size={18} />,
        },
        {
          label: "Create",
          path: "/solutions/create",
          agent: "create",
          icon: <IconPencilPlus size={18} />,
        },
        {
          label: "Review",
          path: "/solutions/review",
          agent: "review",
          icon: <IconChecklist size={18} />,
        },
        {
          label: "Quality",
          path: "/solutions/quality",
          agent: "quality",
          icon: <IconRosetteDiscountCheck size={18} />,
        },
      ],
    },
    {
      solution: "validation",
      label: "Validation",
      icon: <IconShieldCheck size={18} />,
      entries: [
        {
          label: "1st line support",
          path: "/solutions/validation/first-line-support",
          agent: "first_line_support",
          icon: <IconHeadset size={18} />,
        },
        {
          label: "2nd line support",
          path: "/solutions/validation/second-line-support",
          agent: "second_line_support",
          icon: <IconRobot size={18} />,
        },
        // Every role: anyone may be named on a run (ADR-0054), and the list
        // shows only the runs the caller owns, follows or is named on.
        // Opening a run stays Developer+ (ADR-0019). One entry: the standalone
        // risk assessment and the settings are a tab and a gear of the same
        // page (ADR-0064, ADR-0069).
        {
          label: "Release validation runs",
          path: "/validation/runs",
          also: ["/validation/risk-assessment", "/validation/settings"],
          agent: "release_validation",
          icon: <IconClipboardCheck size={18} />,
        },
        // A tester is any Entra user, and this is their whole view of validation.
        {
          label: "My tests",
          path: "/validation/my-tests",
          agent: "my_tests",
          icon: <IconChecklist size={18} />,
        },
        // The Administrators' queue for 1st and 2nd line support: questions
        // the assistant could not answer, Veeva user changes awaiting approval
        // (ADR-0031, ADR-0038, ADR-0069). Not an agent: the role decides.
        {
          label: "Support tickets",
          path: TICKETS_PATH,
          adminOnly: true,
          icon: <IconTicket size={18} />,
        },
      ],
    },
    {
      solution: "clinical",
      label: "Clinical",
      icon: <IconStethoscope size={18} />,
      entries: [
        {
          label: "eTMF intake",
          path: "/solutions/clinical/etmf-intake",
          agent: "etmf_intake",
          icon: <IconFileImport size={18} />,
        },
        {
          label: "eTMF completeness",
          path: "/solutions/clinical/etmf-completeness",
          agent: "etmf_completeness",
          icon: <IconLayoutGrid size={18} />,
        },
        // eTMF artifacts assigned to the signed-in user for review (ADR-0043).
        {
          label: "Assignments",
          path: "/solutions/clinical/assignments",
          agent: "etmf_assignments",
          icon: <IconUserCheck size={18} />,
        },
      ],
    },
    {
      // The records archive. Search first: for an end user the archive is
      // something you search, not an inventory you browse; the container list
      // is the records manager's view. Which archive a person can open is a
      // per-container grant, checked server-side — the nav only shows the door.
      solution: "archive",
      label: "Archive",
      icon: <IconArchive size={18} />,
      entries: [
        {
          label: "Search & Retrieve",
          path: "/archive",
          agent: "archive_search",
          also: ["/archive/search"],
          icon: <IconSearch size={18} />,
        },
        {
          label: "Archives",
          path: "/archive/containers",
          agent: "archive_containers",
          icon: <IconArchive size={18} />,
        },
      ],
    },
  ];
}

/** The connection-backed browsers. Each opens its own picker page; the
 *  connection is chosen inside, not before. */
